import { useFloatingBoundary } from "@faire/slate/private/floating/FloatingBoundaryContext";
import { useFloatingContext } from "@faire/slate/private/floating/FloatingContext";
import { getFloatingBoundaryDetectOverflowOptions } from "@faire/slate/private/floating/getFloatingBoundaryDetectOverflowOptions";
import { getFloatingHideStyles } from "@faire/slate/private/floating/getFloatingHideStyles";
import { mergeFloatingDetectOverflowPadding } from "@faire/slate/private/floating/mergeFloatingDetectOverflowPadding";
import { getFloatingPlacement } from "@faire/slate/private/floating/utils";
import {
  arrow,
  autoUpdate,
  flip,
  hide,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react";
import React, {
  createContext,
  useContext,
  useRef,
  useMemo,
  useLayoutEffect,
} from "react";

import {
  POPOVER_OFFSET,
  POPOVER_ARROW_OFFSET,
  POPOVER_FLIP_DETECTION_OFFSET,
} from "./consts";
import { IPopoverOptions } from "./types";

export const PopoverContext = ({
  children,
  position,
  alignment,
  hideWhenReferenceHidden = false,
}: React.PropsWithChildren<IPopoverOptions>) => {
  const value = useInitContext({
    position,
    alignment,
    hideWhenReferenceHidden,
  });
  return <Context value={value}>{children}</Context>;
};

export const usePopoverContext = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error("PopoverContext not found");
  }

  return context;
};

const Context = createContext<ReturnType<typeof useInitContext> | undefined>(
  undefined
);

function useInitContext({
  position,
  alignment,
  hideWhenReferenceHidden,
}: IPopoverOptions) {
  const arrowRef = useRef<SVGSVGElement>(null);

  const { floatingRootContext, setFloatingStyles } = useFloatingContext();
  const boundary = useFloatingBoundary();
  const hasBoundary = boundary.element !== null;
  const shouldHide = hasBoundary && hideWhenReferenceHidden;

  const middleware = useMemo(() => {
    const boundaryOptions = getFloatingBoundaryDetectOverflowOptions(boundary);
    const overflowOptions = hasBoundary
      ? {
          ...boundaryOptions,
          padding: mergeFloatingDetectOverflowPadding(
            POPOVER_FLIP_DETECTION_OFFSET,
            boundary.padding
          ),
        }
      : {};

    const items = [
      offset({ mainAxis: POPOVER_OFFSET, alignmentAxis: POPOVER_ARROW_OFFSET }),
      flip({
        fallbackAxisSideDirection: "start",
        fallbackStrategy: "initialPlacement",
        padding: POPOVER_FLIP_DETECTION_OFFSET,
        ...overflowOptions,
      }),
      shift({ padding: POPOVER_FLIP_DETECTION_OFFSET, ...overflowOptions }),
      arrow({ element: arrowRef }),
    ];

    if (shouldHide) {
      items.push(hide({ ...overflowOptions }));
    }

    return items;
  }, [arrowRef, boundary, hasBoundary, shouldHide]);

  const { floatingStyles, context, middlewareData } = useFloating({
    rootContext: floatingRootContext,
    placement: getFloatingPlacement(position, alignment),
    whileElementsMounted: autoUpdate,
    middleware,
  });

  const referenceHidden = middlewareData.hide?.referenceHidden;

  useLayoutEffect(() => {
    setFloatingStyles({
      ...floatingStyles,
      ...(shouldHide ? getFloatingHideStyles({ referenceHidden }) : {}),
    });
  }, [shouldHide, floatingStyles, referenceHidden, setFloatingStyles]);

  return useMemo(
    () => ({
      arrowRef,
      floatingContext: context,
      middlewareData,
    }),
    [arrowRef, context, middlewareData]
  );
}
